import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as Duplicate } from 'assets/icons/duplicate.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as SendIcon } from 'assets/icons/send.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import IconButton from 'components/IconButton/IconButton';
import i18n from 'i18n';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';
import { useDuplicateSurveyMutation } from 'store/slices/survey/apis/surveyApi';

import ConfirmModal from '../ConfirmModal/ConfirmModal';
import ShareModal from '../MaterialAssessmentSurveyBase/ShareModal/ShareModal';

import type ITableMAQuestionActions from './interfaces/ITableMAQuestionActions';

import styles from './scss/TableMaterialityAssessmentQuestion.module.scss';

function TableMAQuestionActions({
  questionItem,
  onEdit,
  onDelete,
  onViewMode,
  onShare,
  vcOptions,
  disableActions,
}: ITableMAQuestionActions) {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [duplicateSurvey] = useDuplicateSurveyMutation();

  const deleteHandler = () => {
    if (onDelete) {
      onDelete(questionItem.id);
    }
    setDeleteModalOpen(false);
  };

  const duplicateHandler = () => {
    duplicateSurvey({ id: questionItem.id })
      .unwrap()
      .then(() => {
        toast.success(i18n.t('notifications.surveyHasBeenDuplicated'));
      })
      .catch(() => {
        toast.error(i18n.t('notifications.errorDuplicatingSurvey'));
      });
  };

  const shareHandler = () => {
    setShareModalOpen(true);
  };

  const handleShare = (emails: string[], vcIds?: number[]) => {
    if (onShare) {
      onShare(questionItem.id, emails, vcIds || []);
    }
    setShareModalOpen(false);
  };

  return (
    <>
      <div className={styles.actionButtons}>
        {onEdit ? (
          <>
            <IconButton
              icon={<EyeIcon />}
              onClick={() => onViewMode && onViewMode(questionItem.id)}
              disabled={disableActions}
            />
            <IconButton
              icon={<Duplicate />}
              onClick={() => setDuplicateModalOpen(true)}
              disabled={disableActions}
            />
            <IconButton
              icon={<PencilIcon />}
              disabled={questionItem.status === 'done' || disableActions}
              onClick={() => onEdit && onEdit(questionItem.id)}
            />
          </>
        ) : (
          <IconButton
            icon={<SendIcon />}
            onClick={shareHandler}
            disabled={disableActions}
          />
        )}
        {onDelete ? (
          <IconButton
            icon={<TrashIcon />}
            onClick={() => setDeleteModalOpen(true)}
            disabled={disableActions}
          />
        ) : null}
      </div>
      <ConfirmModal
        open={isDuplicateModalOpen}
        title={t('common.confirmModalTitle', { name: questionItem.name })}
        onConfirm={duplicateHandler}
        onReject={() => setDuplicateModalOpen(false)}
      />
      <DeleteModal
        open={isDeleteModalOpen}
        onReject={() => setDeleteModalOpen(false)}
        onConfirm={deleteHandler}
        name={questionItem.name}
        title="deleteSurvey"
        isLoading={false}
      />
      <ShareModal
        open={isShareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onShare={handleShare}
        vcOptions={vcOptions}
      />
    </>
  );
}

export default TableMAQuestionActions;
